/**
 * Profile Snapshot - Instant profile access via sessionStorage
 * Eliminates DB roundtrips for navbar/UI that needs current profile immediately
 */

const SNAPSHOT_KEY = 'active-profile-snapshot';
const AVATAR_CHANGE_EVENT = 'profile-avatar-changed';

export interface ProfileSnapshot {
  id: string;
  name: string;
  avatar_url: string | null;
  profile_type: 'adult' | 'child';
  timestamp: number;
}

/**
 * Save the active profile snapshot for instant access
 * Call this when user selects a profile on /launch or switches profiles
 */
export function saveProfileSnapshot(profile: {
  id: string;
  name: string;
  avatar_url: string | null;
  profile_type?: 'adult' | 'child';
}): void {
  const snapshot: ProfileSnapshot = {
    id: profile.id,
    name: profile.name,
    avatar_url: profile.avatar_url,
    profile_type: profile.profile_type || 'adult',
    timestamp: Date.now()
  };
  
  try {
    sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
  } catch {
    // sessionStorage not available (SSR, private mode, etc.)
  }
}

/**
 * Get the profile snapshot instantly (sync, no DB call)
 * Returns null if no snapshot exists
 */
export function getProfileSnapshot(): ProfileSnapshot | null {
  try {
    const data = sessionStorage.getItem(SNAPSHOT_KEY);
    if (!data) return null;
    
    const snapshot = JSON.parse(data) as ProfileSnapshot;
    
    // Validate snapshot has required fields
    if (!snapshot.id || !snapshot.name) return null;
    
    return snapshot;
  } catch {
    return null;
  }
}

/**
 * Check if we have a valid snapshot (sync check)
 */
export function hasProfileSnapshot(): boolean {
  return getProfileSnapshot() !== null;
}

/**
 * Clear the profile snapshot (on logout)
 */
export function clearProfileSnapshot(): void {
  try {
    sessionStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Update just the avatar URL in the snapshot (after preload confirms)
 * Also dispatches a custom event so the navbar can animate the refresh
 */
export function updateSnapshotAvatar(avatarUrl: string | null): void {
  const snapshot = getProfileSnapshot();
  if (snapshot) {
    const oldAvatarUrl = snapshot.avatar_url;
    snapshot.avatar_url = avatarUrl;
    snapshot.timestamp = Date.now();
    try {
      sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
      
      // Dispatch event if avatar actually changed
      if (oldAvatarUrl !== avatarUrl) {
        window.dispatchEvent(new CustomEvent(AVATAR_CHANGE_EVENT, {
          detail: { oldUrl: oldAvatarUrl, newUrl: avatarUrl, profileId: snapshot.id }
        }));
      }
    } catch {
      // Ignore
    }
  }
}

/**
 * Subscribe to avatar change events
 * Returns unsubscribe function
 */
export function onAvatarChange(callback: (detail: { oldUrl: string | null; newUrl: string | null; profileId: string }) => void): () => void {
  const handler = (e: Event) => {
    const customEvent = e as CustomEvent;
    callback(customEvent.detail);
  };
  
  window.addEventListener(AVATAR_CHANGE_EVENT, handler);
  return () => window.removeEventListener(AVATAR_CHANGE_EVENT, handler);
}

/**
 * Trigger avatar refresh notification manually
 * Call this after successfully updating avatar in DB
 */
export function notifyAvatarChange(profileId: string, newAvatarUrl: string | null): void {
  const snapshot = getProfileSnapshot();
  if (snapshot && snapshot.id === profileId) {
    updateSnapshotAvatar(newAvatarUrl);
  }
}
